// Mirrors backend Session 39 platform types (shared/types/platform.types.ts).
// Used by the super-admin console + the dynamic branding system.

export const PLAN_CODES = ['trial', 'basic', 'standard', 'premium'] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

/** Plans that already include every module — they can never carry add-ons. */
export const NO_ADDON_PLANS: readonly PlanCode[] = ['premium', 'trial'];
export type BillingCycle = 'monthly' | 'quarterly' | 'half_yearly' | 'annually';
export type PaymentStatus =
  | 'active'
  | 'overdue'
  | 'grace_period'
  | 'suspended'
  | 'cancelled';
export type TenantStatus =
  | 'active'
  | 'suspended'
  | 'onboarding'
  | 'trial'
  | 'expired'
  | 'deleted';
export type SchoolType =
  | 'private'
  | 'government'
  | 'aided'
  | 'central'
  | 'international';
export type SchoolMedium = 'english' | 'hindi' | 'regional' | 'bilingual';
export type SchoolGender = 'co_ed' | 'boys' | 'girls';
export type SupportTier = 'standard' | 'priority' | 'dedicated';

// ── Modules (feature keys) ────────────────────────────────────────────────────
// Mirrors API shared/constants/platform.ts. CORE modules are always on and never
// gated; the rest come from the school's plan or a priced add-on.
export const FEATURE_KEYS = [
  // core
  'students',
  'teachers',
  'academicSetup',
  'attendance',
  'calendar',
  'announcements',
  'leave',
  'birthdays',
  'teacherApp',
  'parentApp',
  'customBranding',
  // basic
  'exams',
  'reportCards',
  'fees',
  // standard
  'timetable',
  'finance',
  'onlinePayment',
  'admissions',
  'visitors',
  'smsNotifications',
  'whatsappNotifications',
  // premium
  'transport',
  'library',
  'apiAccess',
  'prioritySupport',
  'dedicatedDb',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];
/** A module is identified by its feature key. */
export type ModuleKey = FeatureKey;

export const MODULE_CATEGORIES = [
  'core',
  'academics',
  'finance',
  'front_office',
  'communication',
  'operations',
  'platform',
] as const;
export type ModuleCategory = (typeof MODULE_CATEGORIES)[number];

/** Package tiers, lowest first. A plan of tier T includes every module of tier ≤ T. */
export const MODULE_TIERS = ['core', 'basic', 'standard', 'premium'] as const;
export type ModuleTier = (typeof MODULE_TIERS)[number];

export type ModuleStatus = 'live' | 'coming_soon';

/** Static catalogue entry (labels/tiers render without a request). */
export interface ModuleCatalogEntry {
  key: FeatureKey;
  label: string;
  category: ModuleCategory;
  tier: ModuleTier;
  isCore: boolean;
  status: ModuleStatus;
}

/** GET /platform/modules row — the catalogue merged with editable add-on settings. */
export interface ModuleCatalogItem extends ModuleCatalogEntry {
  addOnEligible: boolean;
  /** INR / month when sold as an add-on. */
  addOnMonthly: number;
  updatedAt: string | null;
}

const core = (key: FeatureKey, label: string): ModuleCatalogEntry => ({
  key,
  label,
  category: 'core',
  tier: 'core',
  isCore: true,
  status: 'live',
});
const paid = (
  key: FeatureKey,
  label: string,
  category: ModuleCategory,
  tier: Exclude<ModuleTier, 'core'>,
  status: ModuleStatus = 'live',
): ModuleCatalogEntry => ({ key, label, category, tier, isCore: false, status });

/** The module catalogue (copy of the API's MODULE_CATALOG, minus pricing). */
export const MODULE_CATALOG: readonly ModuleCatalogEntry[] = [
  core('students', 'Students'),
  core('teachers', 'Teachers & Staff'),
  core('academicSetup', 'Academic Setup'),
  core('attendance', 'Attendance & Holidays'),
  core('calendar', 'School Calendar'),
  core('announcements', 'Announcements & Notifications'),
  core('leave', 'Leave Management'),
  core('birthdays', 'Birthdays'),
  core('teacherApp', 'Teacher App'),
  core('parentApp', 'Parent & Student App'),
  core('customBranding', 'Custom Branding'),
  paid('exams', 'Exams & Marks', 'academics', 'basic'),
  paid('reportCards', 'Report Cards', 'academics', 'basic'),
  paid('fees', 'Fee Management', 'finance', 'basic'),
  paid('timetable', 'Timetable & Substitutions', 'academics', 'standard'),
  paid('finance', 'Expenses & Finance', 'finance', 'standard'),
  paid('onlinePayment', 'Online Payments', 'finance', 'standard'),
  paid('admissions', 'Admissions', 'front_office', 'standard'),
  paid('visitors', 'Visitor Management', 'front_office', 'standard'),
  paid('smsNotifications', 'SMS Notifications', 'communication', 'standard'),
  paid('whatsappNotifications', 'WhatsApp Notifications', 'communication', 'standard'),
  paid('transport', 'Transport', 'operations', 'premium', 'coming_soon'),
  paid('library', 'Library', 'operations', 'premium', 'coming_soon'),
  paid('apiAccess', 'API Access', 'platform', 'premium'),
  paid('prioritySupport', 'Priority Support', 'platform', 'premium'),
  paid('dedicatedDb', 'Dedicated Database', 'platform', 'premium'),
];

/** GET /auth/entitlements — what the signed-in user's school may use. */
export interface Entitlements {
  /** null for a super-admin outside any school. */
  plan: PlanCode | null;
  /** e.g. "Basic" or "Basic + Custom". */
  planName: string;
  isCustom: boolean;
  addOns: FeatureKey[];
  /** Every enabled module key (core included). */
  modules: FeatureKey[];
}

export const CORE_MODULES: ReadonlySet<FeatureKey> = new Set(
  MODULE_CATALOG.filter((m) => m.isCore).map((m) => m.key),
);

/** Modules that may never be add-ons, whatever the catalogue says. */
export const NEVER_ADDON_MODULES: readonly FeatureKey[] = ['dedicatedDb'];

export function catalogEntry(key: string): ModuleCatalogEntry | undefined {
  return MODULE_CATALOG.find((m) => m.key === key);
}

/** Catalogue modules grouped by category, in category order (empty groups dropped). */
export function modulesByCategory<T extends { category: ModuleCategory }>(
  items: readonly T[],
): { category: ModuleCategory; label: string; items: T[] }[] {
  return MODULE_CATEGORIES.map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    items: items.filter((m) => m.category === category),
  })).filter((g) => g.items.length > 0);
}

export type PlanFeatures = Record<FeatureKey, boolean>;

// ── Branding ──────────────────────────────────────────────────────────────────
/**
 * Unified branding shape covering BOTH the public login-page endpoint
 * (schoolName/tagline/logo/primaryColor/loginBannerImage) and the fuller
 * logged-in `/auth/tenant-branding` payload (all colours + officialName).
 */
export interface SchoolBranding {
  slug?: string;
  schoolName: string;
  officialName?: string | null;
  tagline?: string | null;
  logo: string | null;
  logoSmall?: string | null;
  primaryColor: string;
  secondaryColor?: string | null;
  headerColor?: string | null;
  sidebarColor?: string | null;
  fontFamily?: string | null;
  loginBannerImage?: string | null;
  receiptHeader?: string | null;
  reportHeader?: string | null;
}

// ── Plans ─────────────────────────────────────────────────────────────────────
export interface PlanPricing {
  monthly: number;
  quarterly: number;
  halfYearly: number;
  annually: number;
}

export interface Plan {
  id: string;
  planCode: PlanCode;
  planName: string;
  description: string;
  maxStudents: number;
  maxTeachers: number;
  maxStorageMB: number;
  maxSmsPerMonth: number;
  features: PlanFeatures;
  pricing: PlanPricing;
  displayOrder: number;
  isActive: boolean;
  isPopular: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── School info sub-shapes ────────────────────────────────────────────────────
export interface SchoolAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface SchoolInfo {
  officialName: string;
  shortName: string;
  tagline: string | null;
  registrationNumber: string | null;
  affiliationNumber: string | null;
  establishedYear: number | null;
  email: string;
  phone: string;
  alternatePhone: string | null;
  website: string | null;
  faxNumber: string | null;
  address: SchoolAddress;
  principalName: string | null;
  principalEmail: string | null;
  principalPhone: string | null;
  schoolType: SchoolType;
  medium: SchoolMedium;
  gender: SchoolGender;
  gradesOffered: { from: string; to: string };
}

/** A higher-tier module sold to a Basic/Standard school, priced per month. */
export interface SubscriptionAddOn {
  module: FeatureKey;
  /** INR / month, locked at the price when it was added. */
  monthlyPrice: number;
  addedAt: string;
  addedBy: string | null;
}

export interface Subscription {
  plan: PlanCode;
  billingCycle: BillingCycle;
  /** The plan's base monthly price (add-ons excluded). */
  pricePerMonth: number;
  /** Cycle total INCLUDING add-ons (discounted like the plan). */
  totalAmount: number;
  addOns: SubscriptionAddOn[];
  /** Σ add-on monthly prices. */
  addOnsMonthly: number;
  discountPercentage: number;
  startDate: string;
  endDate: string;
  nextBillingDate: string;
  paymentStatus: PaymentStatus;
  lastPaymentDate: string | null;
  lastPaymentAmount: number | null;
  trialEndsAt: string | null;
  autoRenew: boolean;
}

export interface Usage {
  currentStudents: number;
  maxStudents: number;
  currentTeachers: number;
  maxTeachers: number;
  storageUsedMB: number;
  maxStorageMB: number;
  smsUsed: number;
  maxSmsPerMonth: number;
  lastSyncedAt: string | null;
}

export interface Onboarding {
  onboardedBy: string | null;
  onboardedByName: string | null;
  onboardedAt: string | null;
  setupCompleted: boolean;
  setupSteps: {
    basicInfo: boolean;
    branding: boolean;
    adminAccount: boolean;
    classStructure: boolean;
    feeStructure: boolean;
  };
  notes: string | null;
}

// ── School list / detail ──────────────────────────────────────────────────────
export interface SchoolSummary {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  city: string | null;
  state: string | null;
  plan: PlanCode;
  /** "Basic" or "Basic + Custom" when add-ons are attached. */
  planLabel: string;
  isCustom: boolean;
  addOns: FeatureKey[];
  billingCycle: BillingCycle;
  paymentStatus: PaymentStatus;
  endDate: string;
  setupCompleted: boolean;
  currentStudents: number;
  maxStudents: number;
  currentTeachers: number;
  maxTeachers: number;
  logo: string | null;
  isDemo: boolean;
  createdAt: string;
}

export interface SchoolListSummary {
  total: number;
  active: number;
  trial: number;
  suspended: number;
}

export interface SchoolDetail {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  /** "Basic" or "Basic + Custom" when add-ons are attached. */
  planLabel: string;
  isCustom: boolean;
  dbStrategy: 'shared' | 'dedicated';
  /** Derived module map (plan + add-ons; core always on). */
  features: Record<string, boolean>;
  config: Record<string, unknown>;
  schoolInfo: SchoolInfo | null;
  branding: SchoolBranding;
  subscription: Subscription;
  usage: Usage;
  onboarding: Onboarding;
  isFeatured: boolean;
  isDemo: boolean;
  supportTier: SupportTier;
  accountManager: string | null;
  daysUntilExpiry: number;
  usagePercentage: {
    students: number;
    teachers: number;
    storage: number;
    sms: number;
  };
  isOverLimit: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
export interface ActivityLog {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  userId: string;
  userName: string;
  action: string;
  details: string;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface PlatformDashboard {
  totalSchools: number;
  activeSchools: number;
  trialSchools: number;
  suspendedSchools: number;
  totalStudents: number;
  totalTeachers: number;
  revenue: { currentMonth: number; projected: number; overdue: number };
  planDistribution: Record<string, number>;
  recentActivity: ActivityLog[];
  expiringThisWeek: SchoolSummary[];
  overLimitSchools: SchoolSummary[];
  onboardingTrend: { month: string; newSchools: number }[];
}

// ── Onboarding + impersonation payloads ───────────────────────────────────────
export interface OnboardSchoolPayload {
  name: string;
  slug: string;
  boardAffiliation: string;
  sessionStart?: string;
  planCode: PlanCode;
  billingCycle: BillingCycle;
  /** Priced add-ons (Basic/Standard only). */
  addOns?: FeatureKey[];
  isDemo?: boolean;
  supportTier?: SupportTier;
  schoolInfo: {
    officialName: string;
    shortName: string;
    tagline?: string | null;
    email: string;
    phone: string;
    website?: string | null;
    establishedYear?: number | null;
    affiliationNumber?: string | null;
    address: {
      line1: string;
      line2?: string | null;
      city: string;
      state: string;
      zipCode: string;
      country?: string;
    };
    principalName?: string | null;
    principalEmail?: string | null;
    principalPhone?: string | null;
    schoolType?: SchoolType;
    medium?: SchoolMedium;
    gender?: SchoolGender;
    gradesOffered?: { from: string; to: string };
  };
  branding?: {
    primaryColor?: string;
    logo?: string | null;
    secondaryColor?: string | null;
  };
  admin: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
}

export interface OnboardResult {
  tenant: { id: string; slug: string; name: string; status: string };
  adminCredentials: { email: string; password: string };
  loginUrl: string;
}

export interface ImpersonationResponse {
  impersonationToken: string;
  schoolName: string;
  tenantId: string;
  role: string;
  loginUrl: string;
  expiresIn: string;
}

// ── Display helpers ───────────────────────────────────────────────────────────
export const PLAN_LABELS: Record<PlanCode, string> = {
  trial: 'Trial',
  basic: 'Basic',
  standard: 'Standard',
  premium: 'Premium',
};

export const BILLING_LABELS: Record<BillingCycle, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  annually: 'Annually',
};

export const STATUS_META: Record<
  TenantStatus,
  { label: string; color: string; dot: string }
> = {
  active: { label: 'Active', color: '#16a34a', dot: '🟢' },
  trial: { label: 'Trial', color: '#d97706', dot: '🟡' },
  suspended: { label: 'Suspended', color: '#dc2626', dot: '🔴' },
  expired: { label: 'Expired', color: '#dc2626', dot: '🔴' },
  onboarding: { label: 'Onboarding', color: '#0ea5e9', dot: '🔵' },
  deleted: { label: 'Deleted', color: '#6b7280', dot: '⚫' },
};

export const FEATURE_LABELS = Object.fromEntries(
  MODULE_CATALOG.map((m) => [m.key, m.label]),
) as Record<FeatureKey, string>;

export const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  core: 'Core',
  academics: 'Academics',
  finance: 'Finance',
  front_office: 'Front Office',
  communication: 'Communication',
  operations: 'Operations',
  platform: 'Platform',
};

export const TIER_LABELS: Record<ModuleTier, string> = {
  core: 'Core',
  basic: 'Basic',
  standard: 'Standard',
  premium: 'Premium',
};

/** Number of months billed per cycle. */
export const CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 3,
  half_yearly: 6,
  annually: 12,
};

/** Discount applied to the equivalent monthly price for each cycle. */
export const CYCLE_DISCOUNT: Record<BillingCycle, number> = {
  monthly: 0,
  quarterly: 0.05,
  half_yearly: 0.1,
  annually: 0.2,
};

const PRICING_KEY: Record<BillingCycle, keyof PlanPricing> = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  half_yearly: 'halfYearly',
  annually: 'annually',
};

/** A plan's price for one billing cycle (the API's rule: explicit price, else discounted monthly). */
export function planCyclePrice(plan: Plan, cycle: BillingCycle): number {
  const explicit = plan.pricing[PRICING_KEY[cycle]];
  if (explicit > 0) return explicit;
  return Math.round(plan.pricing.monthly * CYCLE_MONTHS[cycle] * (1 - CYCLE_DISCOUNT[cycle]));
}

/** Add-ons for one billing cycle, discounted like the plan. */
export function addOnsCyclePrice(addOnsMonthly: number, cycle: BillingCycle): number {
  return Math.round(addOnsMonthly * CYCLE_MONTHS[cycle] * (1 - CYCLE_DISCOUNT[cycle]));
}

/**
 * Modules a school on `plan` may buy as add-ons: Basic/Standard only; live,
 * eligible, non-core modules the base plan doesn't already include.
 */
export function addOnCandidates(
  plan: Plan | undefined,
  modules: readonly ModuleCatalogItem[],
): ModuleCatalogItem[] {
  if (!plan || NO_ADDON_PLANS.includes(plan.planCode)) return [];
  return modules.filter(
    (m) =>
      !m.isCore &&
      m.status === 'live' &&
      m.addOnEligible &&
      !NEVER_ADDON_MODULES.includes(m.key) &&
      !plan.features[m.key],
  );
}

/** Compact Indian currency: ₹12.8L, ₹1.4Cr, ₹4,999. */
export function formatINRShort(n: number): string {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

/** Full Indian currency with grouping: ₹96,000. */
export function formatINR(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}

/** Compact count: 1,42,000 (Indian grouping). */
export function formatCount(n: number): string {
  return n.toLocaleString('en-IN');
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** "In 3 days" / "In 2 months" / "Expired". */
export function expiryLabel(days: number): string {
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 45) return `In ${days} days`;
  const months = Math.round(days / 30);
  return `In ${months} month${months > 1 ? 's' : ''}`;
}
